import { template as template_27fb2fd79b7448d484be606b2b04109e } from "@ember/template-compiler";
import moment from 'moment-timezone';
import Component from '@glimmer/component';
import { service } from '@ember/service';
import FilterWrapper from 'eflex/components/filters/filter-wrapper';
import { t } from 'ember-intl';
import { isPresent } from '@ember/utils';
import { or } from 'ember-truth-helpers';
import { tracked } from '@glimmer/tracking';
import Portal from 'ember-stargate/components/portal';
import SearchTag from 'eflex/components/filters/search-tag';
import { buildValidations, validator } from '@eflexsystems/ember-tracked-validations';
import { dateRangeCorrectness } from 'eflex/util/validators';
@buildValidations({
    beginDate: [
        validator('inline', {
            validate (beginDate, options, config) {
                return dateRangeCorrectness(config, beginDate, config?.endDate, 'filters.invalidBeginDate');
            }
        })
    ],
    endDate: [
        validator('inline', {
            validate (endDate, options, config) {
                return dateRangeCorrectness(config, config?.beginDate, endDate, 'filters.invalidEndDate');
            }
        })
    ]
})
class DateRangeFilter extends Component {
    @service
    intl;
    @tracked
    isDisplayed;
    @tracked
    range_selected;
    constructor(){
        super(...arguments);
        this.args.initParam('beginDate', this.args.beginDate);
        this.args.initParam('endDate', this.args.endDate);
        this.isDisplayed = this.args.beginDate || this.args.endDate;
        this.range_selected = this.findRange;
    }
    get searchTagBeginDate() {
        const value = this.args.beginDate ? new Date(this.args.beginDate) : null;
        return value ? this.intl.t('breadcrumbs.beginDate', {
            beginDate: moment(value).format('lll')
        }) : null;
    }
    get searchTagEndDate() {
        const value = this.args.endDate ? new Date(this.args.endDate) : null;
        return value ? this.intl.t('breadcrumbs.endDate', {
            endDate: moment(value).format('lll')
        }) : null;
    }
    onDisplayedChanged = ()=>{
        this.isDisplayed = !this.isDisplayed;
    };
    get findRange() {
        const beginDate = this.beginDate;
        const endDate = this.endDate;
        if (!beginDate || !endDate) {
            return null;
        }
        return this.dateRangeOptions.find((option)=>moment(beginDate).isSame(option.beginDate, 'day') && moment(endDate).isSame(option.endDate, 'day'));
    }
    get beginDate() {
        const beginDate = this.args.getParam('beginDate');
        return beginDate ? new Date(beginDate) : null;
    }
    get endDate() {
        const endDate = this.args.getParam('endDate');
        return endDate ? new Date(endDate) : null;
    }
    dateRangeOptions = [
        {
            label: 'datetime.today',
            value: 'today',
            beginDate: moment().startOf('day').toDate(),
            endDate: moment().add(1, 'hours').toDate()
        },
        {
            label: 'datetime.yesterday',
            value: 'yesterday',
            beginDate: moment().subtract(1, 'days').startOf('day').toDate(),
            endDate: moment().subtract(1, 'days').endOf('day').toDate()
        },
        {
            label: 'datetime.lastweek',
            value: 'week',
            beginDate: moment().subtract(7, 'days').toDate(),
            endDate: moment().add(1, 'hours').toDate()
        },
        {
            label: 'datetime.lastmonth',
            value: 'month',
            beginDate: moment().subtract(1, 'months').toDate(),
            endDate: moment().add(1, 'hours').toDate()
        },
        {
            label: 'datetime.lastquarter',
            value: 'quarter',
            beginDate: moment().subtract(3, 'months').toDate(),
            endDate: moment().add(1, 'hours').toDate()
        }
    ];
    updateParam = (key, value)=>{
        this.args.updateParam(key, value);
        if (this.validations && this.args.setIsInvalid) {
            this.args.setIsInvalid(this.validations?.isInvalid);
        }
    };
    onChangeBeginDate = (beginDate)=>{
        this.updateParam('beginDate', beginDate?.toJSON());
    };
    onChangeEndDate = (endDate)=>{
        this.updateParam('endDate', endDate?.toJSON());
    };
    onSelected = (range)=>{
        this.updateParam('beginDate', range?.beginDate);
        this.updateParam('endDate', range?.endDate);
    };
    clear = ()=>{
        this.onSelected(null);
        this.updateParam('beginDate', null);
        this.updateParam('endDate', null);
    };
    static{
        template_27fb2fd79b7448d484be606b2b04109e(`
    <Portal @target="filter-fields">
      <FilterWrapper
        class="eflex-date-range-criteria"
        @class="align-items-end"
        @form={{@form}}
        @label="datetime"
        @isDisplayed={{this.isDisplayed}}
        @onChecked={{this.onDisplayedChanged}}
        @hasValue={{or (isPresent this.beginDate) (isPresent this.endDate)}}
        @clear={{this.clear}}
        ...attributes
      >
        <div class="input-group">
          <div class="col">
            <div class="row">
              <div class="col">
                <label for="date-range" class="fs-4 text-nowrap mb-1">
                  {{t "controls.daterange.range"}}
                </label>
                <@form.element
                  id="date-range"
                  class="w-100"
                  @controlType="power-select"
                  @options={{this.dateRangeOptions}}
                  @value={{this.findRange}}
                  @onChange={{this.onSelected}}
                as |el|>
                  <el.control
                    class="date-range sm"
                    @placeholder={{t "controls.daterange.select"}}
                    as |range|
                  >
                    {{t range.label}}
                  </el.control>
                </@form.element>
              </div>

              <div class="col">
                <label for="start-date" class="fs-4 text-nowrap mb-1">
                  {{t "datetime.begin"}}
                </label>

                <@form.element
                  id="start-date"
                  class="start-date w-100"
                  @controlType="date-picker"
                  @property="beginDate"
                  @onChange={{this.onChangeBeginDate}}
                  @model={{this}}
                as |el|>
                  <el.control
                    class="form-control-sm"
                    @altInput={{true}}
                    @enableTime={{true}}
                    @allowInput={{true}}
                    @dateFormat="Z"
                    @altFormat="M j, Y h:iK"
                  />
                </@form.element>
              </div>
              <div class="col">
                <label for="end-date" class="fs-4 text-nowrap mb-1">
                  {{t "datetime.end"}}
                </label>

                <@form.element
                  id="end-date"
                  class="end-date w-100"
                  @controlType="date-picker"
                  @property="endDate"
                  @onChange={{this.onChangeEndDate}}
                  @model={{this}}
                as |el|>
                  <el.control
                    class="form-control-sm"
                    @altInput={{true}}
                    @enableTime={{true}}
                    @allowInput={{true}}
                    @dateFormat="Z"
                    @altFormat="M j, Y h:iK"
                  />
                </@form.element>
              </div>
            </div>
          </div>
        </div>
      </FilterWrapper>
    </Portal>

    <Portal @target="search-badge-tags">
      <SearchTag
        class='beginDate'
        @isDisplayed={{this.beginDate}}
        @text={{this.searchTagBeginDate}}
      />
      <SearchTag
        class='endDate'
        @isDisplayed={{this.endDate}}
        @text={{this.searchTagEndDate}}
      />
    </Portal>
  `, {
            component: this,
            eval () {
                return eval(arguments[0]);
            }
        });
    }
}
export default DateRangeFilter;
